import { useState, useEffect, Key } from 'react';
import { useTranslation } from 'react-i18next';
import MultiSelectDropdown from 'src/components/dropdowns/MultiSelectDowndown';
import { QuestionTherapy } from 'src/interfaces/therapyInterface';
import {
  Question,
  QuestionAnswerPayload,
  Option,
  AnswerPayload,
} from 'src/interfaces/therapyInterface';

const TherapyQuestionsDetails = (props: QuestionTherapy) => {
  // const dispatch: ThunkDispatch<RootState, unknown, AnyAction> = useDispatch();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [loading] = useState(false);
  const [religious, setReligious] = useState(false);

  const { t } = useTranslation(['therapyDetail']);

  const { therapyQuestions, isEditing, onSubmit } = props;

  // Changed the answer state structure to track selected option IDs
  const [selectedOptions, setSelectedOptions] = useState<Record<number, string | string[]>>({});

  // New useEffect to initialize answers when therapyQuestions contain answers
  useEffect(() => {
    if (!therapyQuestions?.length) return;

    const initialAnswers: Record<number, string | string[]> = {};

    therapyQuestions.forEach((question: Question) => {
      if (question.answers && question.answers.length > 0) {
        // Check if it's a multi-select question
        if (getQuestionType(question) === 'multiselect') {
          initialAnswers[question.id] = question.answers.map((answer) => answer.value);
        } else {
          // For radio and dropdown, we just need the first answer's value
          initialAnswers[question.id] = question.answers[0].value;
        }

        if (question.id === 7) {
          // Check if question 7 is answered "No"
          setReligious(
            initialAnswers[question.id] ===
              'No, I do not include religion/spirituality in my practice',
          );
        }
      }
    });
    setSelectedOptions(initialAnswers);
  }, [therapyQuestions]);

  // Fetch therapyQuestions from the backend

  // Check if all answers are filled
  useEffect(() => {
    if (therapyQuestions.length === 0) return;

    // Find question 7 and 8 IDs
    const q8 = therapyQuestions.find((q: any) => q.id === 8);

    // Check if question 7 is answered "No"
    const q7No = selectedOptions[7] === 'No, I do not include religion/spirituality in my practice';

    // Build a list of question IDs to check
    let requiredIds = therapyQuestions.map((q: any) => q.id);
    if (q7No && q8) {
      // Remove question 8 from required if q7 is "No"
      requiredIds = requiredIds.filter((id: any) => id !== 8);
    }

    const allAnswered = requiredIds.every((id: any) => {
      const value = selectedOptions[id];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '' && value !== undefined;
    });

    setIsComplete(allAnswered);
  }, [selectedOptions, therapyQuestions]);

  const handleOptionChange = (questionId: number, value: string | string[]) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [questionId]: value,
    }));
    if (questionId === 7) {
      setReligious(value === 'No, I do not include religion/spirituality in my practice');
    }
  };

  const handleNext = () => {
    if (therapyQuestions[currentQuestion].id === 7 && religious) {
      setCurrentQuestion((prev) => prev + 2);
    } else {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0 && therapyQuestions[currentQuestion].id === 9 && religious) {
      setCurrentQuestion((prev) => prev - 2);
    } else {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const formatAnswersForApi = (): QuestionAnswerPayload[] => {
    return Object.entries(selectedOptions).map(([questionId, selectedValue]) => {
      const questionIdNum = parseInt(questionId);
      const question = therapyQuestions.find((q: any) => q.id === questionIdNum);

      if (!question) return { questionId: questionIdNum, answers: [] };

      // Convert selected values to answer objects with label and value
      let answers: AnswerPayload[] = [];

      if (Array.isArray(selectedValue)) {
        // Handle multi-select
        answers = selectedValue.map((value) => {
          // Find the option matching this value
          const option = question.options.find((opt: Option) => String(opt.value) === value);
          return {
            label: option?.label || '',
            value: option?.value || value,
          };
        });
      } else {
        // Handle single select
        const option = question.options.find((opt: Option) => String(opt.value) === selectedValue);
        if (option) {
          answers = [
            {
              label: option.label,
              value: option.value,
            },
          ];
        }
      }

      return {
        questionId: questionIdNum,
        answers,
      };
    });
  };

  const handleComplete = () => {
    const formattedAnswers = formatAnswersForApi();

    if (onSubmit) {
      onSubmit(formattedAnswers);
    }
  };

  // Function to determine question type
  const getQuestionType = (question: Question): 'radio' | 'dropdown' | 'multiselect' => {
    if (question.id === 2 || question.id === 3) {
      return 'multiselect';
    }
    if (question.id === 4 || question.id === 5 || question.id === 6 || question.id === 8) {
      return 'dropdown';
    }
    return 'radio';
  };

  const renderQuestionInput = (question: Question) => {
    const questionType = getQuestionType(question);
    const existingAnswers = question.answers || [];
    if (questionType === 'radio') {
      return (
        <div className='space-y-4'>
          {question.options.map((option: Option) => {
            const isChecked = selectedOptions[question.id] === String(option.value);
            return (
              <div key={option.value} className='flex items-center mt-6'>
                <input
                  type='radio'
                  id={String(option.value)}
                  name={String(question.id)}
                  value={String(option.value)}
                  checked={isChecked}
                  onChange={(e) => handleOptionChange(question.id, e.target.value)}
                  className='w-4 h-4 text-primary border-gray-300 focus:ring-primary m-1 cursor-pointer'
                />
                <label
                  htmlFor={String(option.value)}
                  className='ml-3 text-lg font-medium text-gray-700'
                >
                  {option.label}
                </label>
              </div>
            );
          })}
        </div>
      );
    } else if (questionType === 'dropdown') {
      // Get current value from either selected options or existing answers
      const currentValue = selectedOptions[question.id] || existingAnswers[0]?.value || '';

      return (
        <div className='mt-6'>
          <select
            id={String(question.id)}
            value={currentValue}
            onChange={(e) => handleOptionChange(question.id, e.target.value)}
            className='block w-full py-3 px-4 text-lg border border-therapistQuestionsBorder bg-background rounded-md shadow-sm focus:border-therapistQuestionsBorder focus:ring-none'
          >
            <option value='' disabled>
              {t('SelectAnOption')}
            </option>
            {question.options.map((option: Option) => (
              <option key={option.id} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    } else if (questionType === 'multiselect') {
      // Get selected values from either current selections or existing answers
      const currentValue = selectedOptions[question.id];
      const existingValues = existingAnswers.map((answer) => answer.value);
      const selectedValues = Array.isArray(currentValue) ? currentValue : existingValues;

      return (
        <div className='mt-6'>
          <MultiSelectDropdown
            options={question.options.map((opt: any) => ({
              value: String(opt.value),
              label: opt.label,
            }))}
            selectedValues={selectedValues}
            onChange={(selectedIds) => handleOptionChange(question.id, selectedIds)}
            placeholder='Select options'
            className='w-full'
          />
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center h-full'>
        <p>{t('LoadingQuestions')}</p>
      </div>
    );
  }

  if (therapyQuestions.length === 0) {
    return (
      <div className='flex justify-center items-center h-full'>
        <p>{t('NoTherapyQuestions')}</p>
      </div>
    );
  }

  return (
    <div
      className='relative flex flex-col min-w-0 break-words w-full shadow-lg rounded-2xl bg-background text-primary'
      style={{ height: '75vh' }}
    >
      <div className='flex flex-col h-full p-8'>
        {currentQuestion === 0 && !isEditing && (
          <div className='mb-8 text-center'>
            <p className='text-lg'>{t('TherapyQuestionsDetailsHeader')}</p>
          </div>
        )}

        <div className='flex-grow overflow-y-auto'>
          <h2 className='text-xl font-semibold'>{therapyQuestions[currentQuestion].text}</h2>
          {therapyQuestions[currentQuestion].isMultipleSelect && (
            <h2 className='text-lg font-normal'>{t('helpText-dropdown')}</h2>
          )}
          {therapyQuestions[currentQuestion].id === 5 && (
            <p className='mt-2 text-gray-600'>{t('helptext-meet-clients')}</p>
          )}

          {renderQuestionInput(therapyQuestions[currentQuestion])}
        </div>

        <div className='border-t'>
          <div className='flex justify-between'>
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className={`flex items-center px-4 py-2 rounded ${
                currentQuestion === 0 ? 'text-gray-400' : 'text-primary hover:bg-gray-100'
              }`}
            >
              {t('Previous')}
            </button>

            {/* Show either Next or Complete button */}
            {currentQuestion === therapyQuestions.length - 1 ? (
              <button
                onClick={handleComplete}
                disabled={!isComplete}
                className={`flex items-center px-6 py-2 rounded ${
                  isComplete
                    ? 'bg-primary text-white hover:bg-opacity-90'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {t('Complete')}
              </button>
            ) : (
              <button
                onClick={handleNext}
                className='flex items-center px-4 py-2 rounded text-primary hover:bg-gray-100'
              >
                {t('Next')}
              </button>
            )}
          </div>

          <div className='mt-4 flex justify-center'>
            <div className='flex space-x-2'>
              {therapyQuestions.map((question: { id: Key | null | undefined }, index: number) => (
                <div
                  key={question.id}
                  className={`w-2 h-2 rounded-full ${
                    index === currentQuestion ? 'bg-primary' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
          {/* <div className='mt-4 flex justify-center'>
            <div className='flex space-x-2'>
              {therapyQuestions.map((index: any) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${index === currentQuestion ? 'bg-primary' : 'bg-gray-300'
                    }`}
                />
              ))}
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default TherapyQuestionsDetails;
